import helpers from './helpers'
import store from '@/store'
import config from '@/config'

const state = config.app.autoconf.STATE.toUpperCase()

/**
 * GeoComply integration service.
 *
 * This class provides an abstraction layer around GeoComply's client SDK,
 * Vuex store actions, and helper functions. It manages:
 * - License retrieval and storage
 * - Initialization of the geolocation process
 * - Validation and continuation of geolocation
 * - Plugin download
 * - Service disconnection
 */

class GeoComply {
  /**
   * Requests a geolocation license and stores it locally.
   *
   * @async
   * @param {string} userID - Unique identifier of the user.
   * @param {string} sessionId - Current session identifier.
   * @param {string} reason - Reason for requesting the license (login, ip changed or periodic check).
   * @returns {Promise<{license: Object}>} - The license object.
   * @throws {Error} - Throws if the license request fails.
   */
  async getLicense (userID, sessionId, reason) {
    try {
      const response = await store.dispatch('geolocationGetLicense', state)
      window.GeoComply.Client.setLicense(response.result.License)
      response.result['logDate'] = new Date()
      localStorage.setItem('userLicense' + state, JSON.stringify(response.result))
      helpers.requestGeoLocation(userID, reason)
      return { license: response.result }
    } catch (error) {
      store.commit('setGeolocationInternalError', error)
      throw error
    }
  }
  /**
   * Initializes the geolocation process.
   *
   * @param {string} userID - Unique identifier of the user.
   * @param {string} sessionId - Current session identifier.
   * @param {string} reason - Reason for initialization.
   * @returns {void} - Does not return a value.
   */
  initGeolocation (userID, sessionId, reason) {
    helpers.startGeolocation(userID, sessionId, reason)
  }
  /**
   * Validates the geolocation packet with the backend.
   *
   * @async
   * @param {Object} geoPacket - The geolocation data packet.
   * @param {string} userID - Unique identifier of the user.
   * @param {string} attemptId - Unique identifier of the attempt.
   * @returns {Promise<{validate: Object}>} - Validation response object.
   * @throws {Error} - Throws if validation fails.
   */
  async validateGeolocation (geoPacket, userID, attemptId) {
    try {
      const response = await store.dispatch('geolocationSubmit', { geoPacket, state, attemptId })
      helpers.validateResponse(response.result, userID)
      return { validate: response.result }
    } catch (error) {
      throw error
    }
  }
  /**
   * Continues the geolocation process (on page reload).
   *
   * @async
   * @returns {Promise<any>} - Continuation result from helpers.
   */
  async continueGeolocation () {
    return helpers.continueGeolocationProcess()
  }
  /**
   * Downloads the GeoComply plugin if required.
   *
   * @async
   * @returns {Promise<any>} - Download result from helpers.
   */
  async downloadPlugin () {
    return helpers.downloadPlugin()
  }
  /**
   * Disconnects the geolocation service.
   *
   * @async
   * @returns {Promise<any>} - Disconnect result from helpers.
   */
  async disconnectService () {
    return helpers.disconnectService()
  }
}
export default GeoComply



// WEBPACK FOOTER //
// ./src/library/geolocation/services/providers/geocomply/index.js